import React, { useState, useCallback, useEffect } from "react";
import {
  Box, Card, Typography, TextField, Button, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, Avatar, Tabs, Tab,
  Divider, Alert, Snackbar, Grid,
} from "@mui/material";
import {
  Add, Refresh, Delete, ArrowBack, PictureAsPdf,
  Group, Person, GroupAdd, EventNote, Schedule,
  Download, RemoveCircle, Assignment,
} from "@mui/icons-material";
import { InvokeApi, apiList } from "../../services/ApiService";
import { Config } from "../../config/Config";
import ClassSectionGrid from "../../components/ClassSectionGrid";
import AsyncStorage from "../../services/AsyncStorage";

const DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

const gradBtn = { background: "linear-gradient(135deg,#1a237e,#1565c0)", borderRadius: 2, fontWeight: 700 };
const outBtn  = { borderRadius: 2, color: "#1a237e", borderColor: "#1a237e" };

export default function Examination() {
  // ── view state ──────────────────────────────────────────────
  const [view, setView]         = useState("grid");   // grid | exam
  const [selected, setSelected] = useState(null);     // class/section row
  const [tab, setTab]           = useState(0);        // 0=Timetable 1=Students 2=PDF
  const [acYears, setAcYears]   = useState([]);
  const [acYear, setAcYear]     = useState("");

  // ── exam master ─────────────────────────────────────────────
  const [exams, setExams]       = useState([]);
  const [exLoading, setExLoad]  = useState(false);
  const [selExam, setSelExam]   = useState(null);
  const [exDialog, setExDialog] = useState(false);
  const [exSaving, setExSaving] = useState(false);
  const [exTitle, setExTitle]   = useState("");
  const [exCenter, setExCenter] = useState("");
  const [exNote, setExNote]     = useState("");

  // ── timetable ───────────────────────────────────────────────
  const [subjects, setSubjects]   = useState([]);
  const [subLoading, setSubLoad]  = useState(false);
  const [subDialog, setSubDialog] = useState(false);
  const [subSaving, setSubSaving] = useState(false);
  const [subDate, setSubDate]     = useState("");
  const [subDay, setSubDay]       = useState("Monday");
  const [subStart, setSubStart]   = useState("");
  const [subEnd, setSubEnd]       = useState("");
  const [subName, setSubName]     = useState("");

  // ── students ────────────────────────────────────────────────
  const [htStudents, setHtStudents]   = useState([]);
  const [stdLoading, setStdLoad]      = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  // ── snackbar ────────────────────────────────────────────────
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const toast = (msg, sev = "success") => setSnack({ open: true, msg, sev });

  // ── load academic years ─────────────────────────────────────────────────
  const loadAcYears = async () => {
    const d = await InvokeApi(Config.main_url + apiList.getAvailableAcademicYears, {});
    const yrs = d?.academic_years || [];
    setAcYears(yrs);
    const cur = yrs.find(y => y.is_current) || yrs[0];
    if (cur) setAcYear(cur.value || "");
  };

  // ── load exam masters ───────────────────────────────────────────────────
  const loadExams = useCallback(async (cls, yr) => {
    if (!cls) return;
    setExLoad(true);
    const d = await InvokeApi(Config.main_url + apiList.getExamMasters, {
      standard: cls.standard, section: cls.section, ac_year: yr,
    });
    setExams(d?.exams || []);
    setExLoad(false);
  }, []);

  // ── load timetable ──────────────────────────────────────────────────────
  const loadSubjects = useCallback(async (examId) => {
    setSubLoad(true);
    const d = await InvokeApi(Config.main_url + apiList.getExamSubjects, { exam_id: examId });
    setSubjects(d?.subjects || []);
    setSubLoad(false);
  }, []);

  // ── load hall-ticket students ───────────────────────────────────────────
  const loadHtStudents = useCallback(async (examId) => {
    setStdLoad(true);
    const d = await InvokeApi(Config.main_url + apiList.getHallTicketStudents, { exam_id: examId });
    setHtStudents(d?.students || []);
    setStdLoad(false);
  }, []);

  // ── select class from grid ──────────────────────────────────────────────
  const handleSelectClass = async (cls) => {
    setSelected(cls);
    setSelExam(null);
    setView("exam");
    await loadAcYears();
    loadExams(cls, acYear);
  };

  // ── open an exam ────────────────────────────────────────────────────────
  const openExam = (exam) => {
    setSelExam(exam);
    setTab(0);
    loadSubjects(exam.ID);
    loadHtStudents(exam.ID);
  };

  // ── create exam ─────────────────────────────────────────────────────────
  const handleCreateExam = async () => {
    if (!exTitle.trim() || !exCenter.trim()) { toast("Title and center are required", "error"); return; }
    setExSaving(true);
    const d = await InvokeApi(Config.main_url + apiList.addExamMaster, {
      title: exTitle, standard: selected.standard, section: selected.section,
      ac_year: acYear, exam_center: exCenter, note: exNote, created_by: "1",
    });
    setExSaving(false);
    if (d?.code === "200") {
      toast("Examination created!");
      setExDialog(false); setExTitle(""); setExCenter(""); setExNote("");
      loadExams(selected, acYear);
    } else toast(d?.message || "Failed", "error");
  };

  // ── delete exam ─────────────────────────────────────────────────────────
  const handleDeleteExam = async (id) => {
    if (!window.confirm("Delete this examination?")) return;
    await InvokeApi(Config.main_url + apiList.deleteExamMaster, { id });
    toast("Deleted"); loadExams(selected, acYear);
  };

  // ── add timetable subject ───────────────────────────────────────────────
  const handleAddSubject = async () => {
    if (!subName.trim() || !subDate || !subStart || !subEnd) {
      toast("All fields are required", "error"); return;
    }
    setSubSaving(true);
    const d = await InvokeApi(Config.main_url + apiList.addExamSubject, {
      exam_id: selExam.ID, date: subDate, day: subDay,
      start_time: subStart, end_time: subEnd, subject: subName,
    });
    setSubSaving(false);
    if (d?.code === "200") {
      toast("Subject added!");
      setSubDialog(false); setSubName(""); setSubDate(""); setSubStart(""); setSubEnd("");
      loadSubjects(selExam.ID);
    } else toast(d?.message || "Failed", "error");
  };

  // ── delete timetable subject ────────────────────────────────────────────
  const handleDeleteSubject = async (id) => {
    if (!window.confirm("Remove this subject from timetable?")) return;
    await InvokeApi(Config.main_url + apiList.deleteExamSubject, { id });
    toast("Removed"); loadSubjects(selExam.ID);
  };

  // ── bulk assign students ────────────────────────────────────────────────
  const handleBulkAssign = async () => {
    if (!window.confirm(`Assign all active students of ${selected.standard} - ${selected.section}?`)) return;
    setBulkLoading(true);
    const d = await InvokeApi(Config.main_url + apiList.bulkAssignHallTicket, {
      exam_id: selExam.ID, standard: selected.standard,
      section: selected.section, ac_year: acYear,
    });
    setBulkLoading(false);
    if (d?.code === "200") {
      toast(`Done! Inserted: ${d.inserted}, Skipped: ${d.skipped}`);
      loadHtStudents(selExam.ID);
    } else toast(d?.message || "No students found", "warning");
  };

  // ── remove student from exam ────────────────────────────────────────────
  const handleRemoveStudent = async (id, name) => {
    if (!window.confirm(`Remove ${name} from this examination?`)) return;
    await InvokeApi(Config.main_url + apiList.removeHallTicketStudent, { id });
    toast("Student removed"); loadHtStudents(selExam.ID);
  };

  // ── build PDF URL with auth token ───────────────────────────────────────
  const openPdf = async (path) => {
    const token = await AsyncStorage.getItem("token");
    const url = `${Config.main_url}${path}`;
    // Open in new tab — browser will send the GET with auth header via fetch
    // Since TCPDF returns binary, we fetch and create a blob URL
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast("Failed to generate PDF", "error"); return; }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
    } catch (e) {
      toast("Error opening PDF", "error");
    }
  };


  // ── GRID VIEW ────────────────────────────────────────────────────────────
  if (view === "grid") return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800} color="#1a237e">Examination</Typography>
        <Typography variant="body2" color="text.secondary">Select a class to manage examinations and hall tickets</Typography>
      </Box>
      <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)", p: 3 }}>
        <ClassSectionGrid onSelect={handleSelectClass} />
      </Card>
    </Box>
  );

  // ── EXAM LIST VIEW ────────────────────────────────────────────────────────
  if (view === "exam" && !selExam) return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Examination</Typography>
          <Typography variant="body2" color="text.secondary">
            {selected?.standard} — Section {selected?.section} · {exams.length} exams
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => setView("grid")} sx={outBtn}>Back</Button>
          <Tooltip title="Refresh">
            <IconButton onClick={() => loadExams(selected, acYear)} sx={{ border: "1px solid #e8eaf6", borderRadius: 2, color: "#1a237e" }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => setExDialog(true)} sx={gradBtn}>
            New Examination
          </Button>
        </Box>
      </Box>

      {/* AC Year tabs */}
      {acYears.length > 0 && (
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          {acYears.map(y => (
            <Chip key={y.value} label={y.label || y.value}
              onClick={() => { setAcYear(y.value); loadExams(selected, y.value); }}
              sx={{
                fontWeight: 600, cursor: "pointer",
                background: y.value === acYear ? "#1a237e" : "#f0f0f0",
                color: y.value === acYear ? "#fff" : "#333",
              }} />
          ))}
        </Box>
      )}

      {/* Exam cards */}
      {exLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress sx={{ color: "#1a237e" }} /></Box>
      ) : exams.length === 0 ? (
        <Card sx={{ borderRadius: 3, p: 6, textAlign: "center", boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>
          <Assignment sx={{ fontSize: 56, color: "#e8eaf6", mb: 1 }} />
          <Typography color="text.secondary">No examinations found. Create one to get started.</Typography>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {exams.map((ex, i) => (
            <Grid item xs={12} sm={6} md={4} key={ex.ID || i}>
              <Card sx={{
                borderRadius: 3, p: 2.5, cursor: "pointer",
                boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                border: "1.5px solid #e8eaf6",
                transition: "all 0.2s",
                "&:hover": { borderColor: "#1a237e", boxShadow: "0 8px 24px rgba(26,35,126,0.15)", transform: "translateY(-2px)" },
              }} onClick={() => openExam(ex)}>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={800} fontSize={15} color="#1a237e" mb={0.5}>{ex.e_title}</Typography>
                    <Typography fontSize={12} color="text.secondary" mb={0.3}>
                      <EventNote sx={{ fontSize: 13, mr: 0.5, verticalAlign: "middle" }} />
                      {ex.e_ac_year}
                    </Typography>
                    <Typography fontSize={12} color="text.secondary">
                      <Schedule sx={{ fontSize: 13, mr: 0.5, verticalAlign: "middle" }} />
                      Center: {ex.e_exam_center}
                    </Typography>
                  </Box>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={e => { e.stopPropagation(); handleDeleteExam(ex.ID); }}
                      sx={{ color: "#c62828", "&:hover": { background: "#ffebee" } }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Chip label="Open" size="small" sx={{ background: "#e8eaf6", color: "#1a237e", fontWeight: 700, cursor: "pointer" }} />
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create exam dialog */}
      <Dialog open={exDialog} onClose={() => setExDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: "#1a237e", borderBottom: "1px solid #f0f0f0" }}>
          New Examination — {selected?.standard} - {selected?.section}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField fullWidth size="small" label="Examination Title *" value={exTitle}
            onChange={e => setExTitle(e.target.value)} placeholder="e.g. Mid Term Exam 2025" autoFocus />
          <TextField fullWidth size="small" label="Exam Center *" value={exCenter}
            onChange={e => setExCenter(e.target.value)} placeholder="e.g. Main Hall" />
          {acYears.length > 0 && (
            <TextField select fullWidth size="small" label="Academic Year" value={acYear}
              onChange={e => setAcYear(e.target.value)}>
              {acYears.map(y => (
                <MenuItem key={y.value} value={y.value}>{y.label || y.value}{y.is_current ? " (Current)" : ""}</MenuItem>
              ))}
            </TextField>
          )}
          <TextField fullWidth size="small" label="Instructions / Note" value={exNote}
            onChange={e => setExNote(e.target.value)} multiline rows={3}
            placeholder="e.g. Students must carry hall ticket and ID proof." />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: "1px solid #f0f0f0" }}>
          <Button onClick={() => setExDialog(false)} variant="outlined" sx={outBtn}>Cancel</Button>
          <Button onClick={handleCreateExam} variant="contained" disabled={exSaving} sx={gradBtn}>
            {exSaving ? <CircularProgress size={20} color="inherit" /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.sev} sx={{ borderRadius: 2 }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );


  // ── EXAM DETAIL VIEW (Timetable | Students | Generate PDF) ───────────────
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">{selExam?.e_title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {selected?.standard} — Section {selected?.section} · {selExam?.e_ac_year} · Center: {selExam?.e_exam_center}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => setSelExam(null)} sx={outBtn}>
          All Exams
        </Button>
      </Box>

      {/* Tabs */}
      <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)", mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: "1px solid #f0f0f0", px: 2 }}
          TabIndicatorProps={{ style: { background: "#1a237e" } }}>
          <Tab label="Timetable" icon={<EventNote />} iconPosition="start"
            sx={{ fontWeight: 700, color: tab === 0 ? "#1a237e" : "text.secondary", minHeight: 56 }} />
          <Tab label={`Students (${htStudents.length})`} icon={<Group />} iconPosition="start"
            sx={{ fontWeight: 700, color: tab === 1 ? "#1a237e" : "text.secondary", minHeight: 56 }} />
          <Tab label="Generate PDF" icon={<PictureAsPdf />} iconPosition="start"
            sx={{ fontWeight: 700, color: tab === 2 ? "#1a237e" : "text.secondary", minHeight: 56 }} />
        </Tabs>

        {/* ── TAB 0: TIMETABLE ── */}
        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography fontWeight={700} color="#1a237e">Exam Timetable ({subjects.length} subjects)</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Refresh">
                  <IconButton onClick={() => loadSubjects(selExam.ID)} sx={{ border: "1px solid #e8eaf6", borderRadius: 2, color: "#1a237e" }}>
                    <Refresh />
                  </IconButton>
                </Tooltip>
                <Button variant="contained" startIcon={<Add />} onClick={() => setSubDialog(true)} sx={gradBtn}>
                  Add Subject
                </Button>
              </Box>
            </Box>
            {subLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress sx={{ color: "#1a237e" }} /></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: "#f5f6fa" }}>
                      {["#", "Date", "Day", "Start Time", "End Time", "Subject / Paper", "Actions"].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, color: "#1a237e", fontSize: 12 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subjects.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 5, color: "text.secondary" }}>
                          No subjects added yet. Click "Add Subject" to begin.
                        </TableCell>
                      </TableRow>
                    ) : subjects.map((s, i) => (
                      <TableRow key={s.ID || i} hover>
                        <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{i + 1}</TableCell>
                        <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{s.e_date}</TableCell>
                        <TableCell>
                          <Chip label={s.e_day} size="small" sx={{ background: "#e8eaf6", color: "#1a237e", fontWeight: 600 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{s.e_start_time}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{s.e_end_time}</TableCell>
                        <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{s.e_subject}</TableCell>
                        <TableCell>
                          <Tooltip title="Remove">
                            <IconButton size="small" onClick={() => handleDeleteSubject(s.ID)} sx={{ color: "#c62828" }}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* ── TAB 1: STUDENTS ── */}
        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography fontWeight={700} color="#1a237e">Assigned Students ({htStudents.length})</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Refresh">
                  <IconButton onClick={() => loadHtStudents(selExam.ID)} sx={{ border: "1px solid #e8eaf6", borderRadius: 2, color: "#1a237e" }}>
                    <Refresh />
                  </IconButton>
                </Tooltip>
                <Button variant="contained" startIcon={bulkLoading ? <CircularProgress size={16} color="inherit" /> : <GroupAdd />}
                  onClick={handleBulkAssign} disabled={bulkLoading} sx={gradBtn}>
                  Bulk Assign Section
                </Button>
              </Box>
            </Box>
            {stdLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress sx={{ color: "#1a237e" }} /></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: "#f5f6fa" }}>
                      {["Sl. No", "Student", "Roll No", "Actions"].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, color: "#1a237e", fontSize: 12 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {htStudents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 5, color: "text.secondary" }}>
                          No students assigned. Use "Bulk Assign Section" to add all students at once.
                        </TableCell>
                      </TableRow>
                    ) : htStudents.map((s, i) => (
                      <TableRow key={s.ID || i} hover>
                        {/* <TableCell>
                          <Avatar sx={{ width: 28, height: 28, fontSize: 12, background: "linear-gradient(135deg,#1a237e,#1565c0)" }}>
                            {s.e_sl_no}
                          </Avatar>
                        </TableCell> */}
                        <TableCell sx={{ fontSize: 13 }}>{i+1}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ width: 30, height: 30, fontSize: 12, background: "linear-gradient(135deg,#1a237e,#1565c0)", color: "white" }}>
                              {s.full_name?.[0]}
                            </Avatar>
                            <Typography fontSize={13} fontWeight={600}>{s.full_name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{s.roll_number || "—"}</TableCell>
                        {/* <TableCell>
                          <Chip label={s.gender || "—"} size="small"
                            sx={{ background: s.gender === "Male" ? "#e3f2fd" : "#fce4ec",
                                  color: s.gender === "Male" ? "#1565c0" : "#c2185b", fontWeight: 600 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{s.dob || "—"}</TableCell> */}
                        <TableCell>
                          <Tooltip title="Remove from exam">
                            <IconButton size="small" onClick={() => handleRemoveStudent(s.ID, s.full_name)} sx={{ color: "#c62828" }}>
                              <RemoveCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* ── TAB 2: GENERATE PDF ── */}
        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography fontWeight={700} color="#1a237e" mb={3}>Generate Hall Tickets</Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {/* Bulk all */}
              <Grid item xs={12} sm={6}>
                <Card onClick={() => openPdf(`/generateHallTicketBulk/${selExam.ID}`)}
                  sx={{ p: 3, borderRadius: 3, cursor: "pointer", border: "2px solid #1a237e",
                    transition: "all 0.2s", "&:hover": { background: "#e8eaf6", transform: "translateY(-2px)" } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: 2, background: "linear-gradient(135deg,#1a237e,#1565c0)",
                      display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <PictureAsPdf sx={{ color: "#fff", fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography fontWeight={800} color="#1a237e">Bulk — All Students</Typography>
                      <Typography fontSize={12} color="text.secondary">{htStudents.length} students · One page each</Typography>
                    </Box>
                    <Download sx={{ ml: "auto", color: "#1a237e" }} />
                  </Box>
                </Card>
              </Grid>
              {/* Section wise */}
              <Grid item xs={12} sm={6}>
                <Card onClick={() => openPdf(`/generateHallTicketBySection/${selExam.ID}/${encodeURIComponent(selected.standard)}/${encodeURIComponent(selected.section)}`)}
                  sx={{ p: 3, borderRadius: 3, cursor: "pointer", border: "2px solid #2e7d32",
                    transition: "all 0.2s", "&:hover": { background: "#e8f5e9", transform: "translateY(-2px)" } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: 2, background: "linear-gradient(135deg,#2e7d32,#43a047)",
                      display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Group sx={{ color: "#fff", fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography fontWeight={800} color="#2e7d32">Section Wise</Typography>
                      <Typography fontSize={12} color="text.secondary">{selected?.standard} — {selected?.section}</Typography>
                    </Box>
                    <Download sx={{ ml: "auto", color: "#2e7d32" }} />
                  </Box>
                </Card>
              </Grid>
            </Grid>

            {/* Individual */}
            <Typography fontWeight={700} color="#1a237e" mb={1.5}>Individual Student</Typography>
            {htStudents.length === 0 ? (
              <Typography color="text.secondary" fontSize={13}>No students assigned yet.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: "#f5f6fa" }}>
                      {["Sl. No", "Student Name", "Roll No", "Download"].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, color: "#1a237e", fontSize: 12 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {htStudents.map((s, i) => (
                      <TableRow key={s.ID || i} hover>
                        <TableCell sx={{ fontSize: 13, fontWeight: 700, color: "#1a237e" }}>{s.e_student_id}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: 12, background: "#e8eaf6", color: "#1a237e" }}>
                              {s.full_name?.[0]}
                            </Avatar>
                            <Typography fontSize={13} fontWeight={600}>{s.full_name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: 13, color: "text.secondary" }}>{s.roll_number || "—"}</TableCell>
                        <TableCell>
                          <Tooltip title="Download Hall Ticket">
                            <IconButton size="small" onClick={() => openPdf(`/generateHallTicket/${selExam.ID}/${s.e_student_id}`)}
                              sx={{ background: "linear-gradient(135deg,#1a237e,#1565c0)", color: "#fff",
                                borderRadius: 1.5, "&:hover": { background: "#0d47a1" } }}>
                              <Download fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Card>

      {/* Add Subject Dialog */}
      <Dialog open={subDialog} onClose={() => setSubDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: "#1a237e", borderBottom: "1px solid #f0f0f0" }}>
          Add Subject to Timetable
        </DialogTitle>
        <DialogContent sx={{ pt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField fullWidth size="small" label="Subject / Paper Name *" value={subName}
            onChange={e => setSubName(e.target.value)} autoFocus placeholder="e.g. Mathematics, English Paper 1" />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Date *" type="date" value={subDate}
                onChange={e => setSubDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Day *" value={subDay}
                onChange={e => setSubDay(e.target.value)}>
                {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map(d => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Start Time *" type="time" value={subStart}
                onChange={e => setSubStart(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="End Time *" type="time" value={subEnd}
                onChange={e => setSubEnd(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: "1px solid #f0f0f0" }}>
          <Button onClick={() => setSubDialog(false)} variant="outlined" sx={outBtn}>Cancel</Button>
          <Button onClick={handleAddSubject} variant="contained" disabled={subSaving} sx={gradBtn}>
            {subSaving ? <CircularProgress size={20} color="inherit" /> : "Add Subject"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.sev} sx={{ borderRadius: 2 }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
